import { useEffect, useState } from 'react';
import { Minus, Plus, ShieldCheck } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { setQuantityBuffer } from '@/hooks/useInventory';
import { searchCollection } from '@services/stockSearch';
import { cn } from '@/lib/utils';

const MAX_BUFFER = 99;

/**
 * Stock confidence buffer card + dialog. The buffer (`shop.quantityBuffer`)
 * is the number of units held back per SKU so the shop never oversells.
 * Saved via the shared `set-quantity-buffer` handler. `null` clears it.
 *
 * The dialog previews how the buffer changes shown quantities against five
 * real in-stock products, pulled from Typesense when the dialog opens.
 */
export function StockAccuracyCard({ shopId, organisationId, currentBuffer = 0, onSaved }) {
	const [open, setOpen] = useState(false);
	const [draft, setDraft] = useState(currentBuffer ?? 0);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	const [preview, setPreview] = useState([]);

	useEffect(() => {
		if (!open) return;
		setDraft(currentBuffer ?? 0);
		setError(null);
		let cancelled = false;
		(async () => {
			if (!shopId || !organisationId) return;
			try {
				const data = await searchCollection(organisationId, shopId, {
					q: '*',
					query_by: 'title, barcode',
					filter_by: 'in_stock:=true',
					per_page: 5,
					page: 1
				});
				if (cancelled) return;
				setPreview(
					data.hits.map(({ document }) => ({
						name: document.title || document.barcode,
						inStock: Number(document.quantity_i ?? 0)
					}))
				);
			} catch {
				if (!cancelled) setPreview([]);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [open, shopId, organisationId, currentBuffer]);

	const save = async () => {
		if (!shopId) return;
		setSaving(true);
		setError(null);
		try {
			await setQuantityBuffer(shopId, draft > 0 ? draft : null);
			setOpen(false);
			onSaved?.();
		} catch (e) {
			setError(e?.response?.data?.message ?? e.message ?? 'Could not save the buffer.');
		} finally {
			setSaving(false);
		}
	};

	return (
		<SoftCard className="!p-5 md:!p-6">
			<div className="flex items-start gap-4">
				<div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-action-soft text-action-soft-foreground">
					<ShieldCheck className="h-5 w-5" />
				</div>
				<div className="min-w-0 flex-1">
					<h3 className="text-[15px] font-bold text-foreground md:text-base">Set your stock confidence buffer</h3>
					<p className="mt-1 text-sm text-muted-foreground">
						How confident are you that what's in your system matches what's actually on your shelves? Channels trust accurate
						retailers with more visibility.
					</p>
					<p className="mt-1 text-sm text-foreground">
						{currentBuffer > 0
							? `Current buffer: ${currentBuffer} unit${currentBuffer === 1 ? '' : 's'} held back per SKU.`
							: 'No buffer set — all stock is visible to customers.'}
					</p>

					<Dialog open={open} onOpenChange={setOpen}>
						<DialogTrigger asChild>
							<button
								type="button"
								className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3.5 py-2 text-xs font-semibold text-foreground transition hover:bg-accent"
							>
								Set buffer manually
							</button>
						</DialogTrigger>
						<DialogContent className="rounded-2xl sm:max-w-xl">
							<DialogHeader>
								<DialogTitle className="text-xl font-bold">Set your stock confidence buffer</DialogTitle>
								<DialogDescription>Choose how many units to hold back per SKU.</DialogDescription>
							</DialogHeader>
							<div className="space-y-5">
								<div>
									<div className="mb-2 text-sm font-bold text-foreground">Buffer amount</div>
									<div className="flex items-center gap-3">
										<div className="inline-flex items-stretch overflow-hidden rounded-xl border border-border">
											<button
												type="button"
												onClick={() => setDraft((d) => Math.max(0, d - 1))}
												className="bg-muted px-3 text-foreground hover:bg-muted/70"
												aria-label="Decrease buffer"
											>
												<Minus className="h-4 w-4" />
											</button>
											<div className="w-16 px-6 py-2 text-center text-base font-semibold tabular-nums">{draft}</div>
											<button
												type="button"
												onClick={() => setDraft((d) => Math.min(MAX_BUFFER, d + 1))}
												className="bg-muted px-3 text-foreground hover:bg-muted/70"
												aria-label="Increase buffer"
											>
												<Plus className="h-4 w-4" />
											</button>
										</div>
										<span className="text-sm text-muted-foreground">units held back so you never oversell</span>
									</div>
								</div>

								{preview.length > 0 ? (
									<div>
										<div className="mb-2 text-sm font-bold text-foreground">How your stock will appear</div>
										<div className="overflow-hidden rounded-xl border border-border">
											<div className="grid grid-cols-[1fr_auto_auto] gap-x-6 bg-muted/60 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
												<div>Product</div>
												<div className="text-right">In stock</div>
												<div className="text-right">Shown to customers</div>
											</div>
											<div className="divide-y divide-border/60">
												{preview.map((p, i) => {
													const shown = Math.max(0, p.inStock - draft);
													return (
														<div
															key={i}
															className="grid grid-cols-[1fr_auto_auto] items-center gap-x-6 px-4 py-2.5 text-sm"
														>
															<div className="truncate text-muted-foreground">{p.name}</div>
															<div className="text-right tabular-nums text-foreground">{p.inStock}</div>
															<div
																className={cn(
																	'text-right font-semibold tabular-nums',
																	shown === 0
																		? 'text-destructive'
																		: shown < 3
																			? 'text-action'
																			: 'text-foreground'
																)}
															>
																{shown}
															</div>
														</div>
													);
												})}
											</div>
										</div>
									</div>
								) : null}

								<div className="rounded-xl bg-muted/60 px-4 py-2.5 text-sm text-muted-foreground">
									{draft === 0
										? 'No buffer — all stock will be visible to customers.'
										: `${draft} unit${draft === 1 ? '' : 's'} per SKU will be held back as a safety buffer.`}
								</div>
								{error ? <p className="text-sm text-destructive">{error}</p> : null}
								<button
									type="button"
									onClick={save}
									disabled={saving}
									className="inline-flex items-center justify-center rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background transition hover:bg-foreground/90 disabled:opacity-50"
								>
									{saving ? 'Saving…' : 'Set buffer'}
								</button>
							</div>
						</DialogContent>
					</Dialog>
				</div>
			</div>
		</SoftCard>
	);
}
